import { useNavigate } from "react-router-dom";

import { Button } from "components/ui/Button";
import { Notice, NoticeText } from "components/ui/Notice/Notice";
import { Pill } from "components/ui/Pill";
import { pathGenerators } from "utils/routes";

import styles from "./DuplicatePatientNotice.module.css";

// One candidate, with the three facts the check returns about it and how strongly it
// matched. "Open this record" abandons what is half-typed on purpose: if this is the
// same person there is no new patient to save, and the admin wanted to be taken there.
const DuplicateCandidateRow = ({ candidate, onOpen }) => (
  <li className={styles.candidate}>
    <div className={styles.candidateFacts}>
      <span className={styles.candidateName}>{candidate.name}</span>
      <Pill
        color={candidate.strength.pillColor.color}
        bg={candidate.strength.pillColor.bg}
        border={candidate.strength.pillColor.border}
      >
        {candidate.strength.label}
      </Pill>
      <span className={styles.candidateDetail}>
        {candidate.dateOfBirth
          ? `DOB ${candidate.dateOfBirth}`
          : "No date of birth recorded"}
      </span>
      {!!candidate.addressLine1 && (
        <span className={styles.candidateDetail}>{candidate.addressLine1}</span>
      )}
    </div>
    <p className={styles.candidateExplanation}>
      {candidate.strength.explanation}
    </p>
    <Button
      variant="outline"
      size="small"
      onClick={() => onOpen(candidate.id)}
      aria-label={`This is the same person — open ${candidate.name}`}
    >
      This is the same person — open this record
    </Button>
  </li>
);

// Shown while a new patient is being typed, never after the save: nothing here refuses
// creation whatever matches (Darren: "this should be an admin making decision"). The
// two ways out are both offered — open the record that already exists, or say this is
// someone new and carry on filling the form.
export const DuplicatePatientNotice = ({ candidates, onDismiss }) => {
  const navigate = useNavigate();

  if (candidates.length === 0) return null;

  const openPatient = (patientId) => {
    navigate(pathGenerators.patient.details(patientId));
  };

  return (
    <Notice
      tone="info"
      testId="patient-duplicate-warning"
      action={
        <Button variant="text" size="small" onClick={onDismiss}>
          This is someone new
        </Button>
      }
    >
      <NoticeText isLead>
        {candidates.length === 1
          ? "A patient with similar details is already registered."
          : `${candidates.length} patients with similar details are already registered.`}
      </NoticeText>
      <NoticeText>
        Matched on name and date of birth only — PPS, GMS and IHI numbers are not
        compared.
      </NoticeText>
      <ul className={styles.candidates}>
        {candidates.map((candidate) => (
          <DuplicateCandidateRow
            key={candidate.id}
            candidate={candidate}
            onOpen={openPatient}
          />
        ))}
      </ul>
    </Notice>
  );
};
